import axios from '@/plugins/axios';
import downloadFile from '@/helpers/downloadFile';
import enumerateArray from '@/helpers/enumerateArray';

const initialState = {
  foundCars: [],
  foundDrivers: [],
  expensesTypes: [],
  incomeTypes: [],
  paymentTypes: [],
  rentTypes: [],
  carStatuses: [],
  list: [],
  currentPage: 1,
  lastPage: 1,
  perPage: 20,
  history: [],
  historyCurrentPage: 1,
  historyLastPage: 1,
  historyPerPage: 100,
  statistics: [],
};

const actions = {
  async searchExistingCars({ commit }, number) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get(`/cars/bynumber?number=${number}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      if (!data.success) throw new Error(data.error.message);

      commit('setFoundCars', data.data.cars);

      return data.data.cars;
    } catch (error) {
      const msg = error.response ? error.response.data.message : error.message;
      return { error: msg };
    }
  },

  async searchExistingDrivers({ commit }, phone) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get(
        `/drivers/byphone/?phone=${encodeURIComponent(phone)}`,
        {
          headers: { Authorization: `Bearer ${token}`, Lang: lang },
        },
      );

      if (!data.success) throw new Error(data.error.message);

      commit('setFoundDrivers', data.data.drivers);
      return data.data.drivers;
    } catch (error) {
      const msg = error.response ? error.response.data.message : error.message;
      return { error: msg };
    }
  },

  async fetchDialogProps({ commit }) {
    try {
      const { data } = await axios.get('workdesk/properties');
      const { properties } = data.data;

      const paymentTypes = Object.entries(properties.payment_types).map(
        ([id, name]) => ({
          id,
          name,
        }),
      );
      const rentTypes = Object.entries(properties.rent_types).map(
        ([id, name]) => ({
          id,
          name,
        }),
      );
      const carStatuses = Object.entries(properties.car_statuses).map(
        ([id, name]) => ({
          id,
          name,
        }),
      );

      commit('setPaymentTypes', paymentTypes);
      commit('setRentTypes', rentTypes);
      commit('setCarStatuses', carStatuses);
      return data;
    } catch (error) {
      return error;
    }
  },

  async fetchTypes({ commit }, isIncome) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const url = isIncome ? 'workdesk/types?income=1' : 'workdesk/types';

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const list = Object.entries(data.data).map(([key, value]) => ({
        key,
        value,
      }));

      if (isIncome) {
        commit('setIncomeTypes', list);
      } else {
        commit(
          'setExpensesTypes',
          list.sort((a, b) => a.value.localeCompare(b.value)),
        );
      }

      return data;
    } catch (error) {
      const msg = error.response ? error.response.data.message : error.message;
      return { error: msg };
    }
  },

  async fetchPaymentTypes({ commit }) {
    try {
      const { data } = await axios.get('workdesk/transaction/payment-types');

      if (!data.success) throw new Error(data.error.message);

      const list = Object.entries(data.data).map(([id, name]) => ({
        id,
        name,
      }));

      commit('setPaymentTypes', list);
      return data;
    } catch (error) {
      return error;
    }
  },

  async fetchList({ commit }, { params, noPrepayment }) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get(`/workdesk?page=${params.page || 1}`, {
        params,
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      // eslint-disable-next-line
      const { items, currentPage, lastPage, perPage } = data.data.items;

      let list = items;

      if (noPrepayment) {
        list = list.filter((item) => {
          const prepayment = item.transactions.find(({ type }) => type === 2);
          return !prepayment;
        });
      }

      commit('setList', enumerateArray(list, currentPage, perPage));
      commit('setCurrentPage', currentPage);
      commit('setLastPage', lastPage);
      commit('setPerPage', perPage);
      return data;
    } catch (error) {
      return error;
    }
  },

  async fetchListItem(_, id) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get(`/workdesk/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return data.data;
    } catch (error) {
      return error;
    }
  },

  async fetchHistory({ commit }, params) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('workdesk/history', {
        params,
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const history = {
        ...data.data.history,
        items: enumerateArray(data.data.history.items),
      };

      commit('setHistory', history);

      return data;
    } catch (error) {
      return error;
    }
  },

  async addNew(_, formData) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.post('workdesk', formData, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      if (!data.success) throw new Error(data.error.message);

      return { message: data.data.message };
    } catch (error) {
      return { error: error.message };
    }
  },

  async addDriverGrade(_, formData) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.post('workdesk/add/driver/grade', formData, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      if (!data.success) throw new Error(data.error.message);

      return { message: data.data.message };
    } catch (error) {
      return { error: error.message };
    }
  },

  async updateItem(_, { id, formData }) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.put(`workdesk/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      if (!data.success) throw new Error(data.error.message);

      return { message: data.data.message };
    } catch (error) {
      const msg = error.response
        ? error.response.data.error.message
        : error.message;

      return { error: msg };
    }
  },

  async deleteItem(_, id) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      await axios.delete(`workdesk/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: true };
    }
  },

  async restoreItem(_, id) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      await axios.get(`workdesk/restore/${id}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return { success: true };
    } catch (error) {
      return { error: true };
    }
  },

  async downloadReport(_, { params, name }) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('workdesk', {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params: {
          ...params,
          export: 1,
        },
      });

      downloadFile(data, name);

      return { success: true };
    } catch {
      return { error: true };
    }
  },

  async fetchStatistics({ commit }, params) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('workdesk/statistics', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params,
      });

      commit('setStatistics', { items: enumerateArray(data.items) });

      return data;
    } catch (error) {
      return error;
    }
  },

  async checkIsVocationAvalible(_, params) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('workdesk/is_vocation', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params,
      });

      return data;
    } catch (error) {
      return error;
    }
  },
};

const mutations = {
  setFoundCars(state, payload) {
    state.foundCars = payload;
  },

  setFoundDrivers(state, payload) {
    state.foundDrivers = payload;
  },

  setExpensesTypes(state, payload) {
    state.expensesTypes = payload;
  },

  setIncomeTypes(state, payload) {
    state.incomeTypes = payload;
  },

  setPaymentTypes(state, payload) {
    state.paymentTypes = payload;
  },

  setRentTypes(state, payload) {
    state.rentTypes = payload;
  },

  setList(state, payload) {
    state.list = payload;
  },

  setCurrentPage(state, payload) {
    state.currentPage = payload;
  },

  setLastPage(state, payload) {
    state.lastPage = payload;
  },

  setPerPage(state, payload) {
    state.perPage = Number(payload);
  },

  setCarStatuses(state, payload) {
    state.carStatuses = payload;
  },

  // eslint-disable-next-line
  setHistory(state, { items, currentPage, lastPage, perPage }) {
    state.history = items;
    state.historyCurrentPage = currentPage;
    state.historyLastPage = lastPage;
    state.historyPerPage = perPage;
  },

  setStatistics(state, { items }) {
    state.statistics = items;
  },
};

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations,
};
